<template>
  <div class="container">
    <div class="title t-xxxl">{{ title }}</div>
    <div v-if="state.currentUser.paid_shoutouts_admin" class="f-sb w-100p p-x1 b-1 m-tx2">
      <div class="f">
        <i class="mi md-16 blue">info</i>
        <div v-if="selfViewing" class="t m-l">{{ adminViewingCopy }}</div>
        <div v-else class="t m-l">You are viewing {{ props.user.name }}'s data as an Admin</div>
      </div>
      <div class="f">
        <donut-selector-prefabs-user class="w-x28" placeholder="View as" :update-function="onUserSelect" />
        <a v-if="!selfViewing" href="/shoutouts/profile" class="button inv m-lx1">My Profile</a>
      </div>
    </div>
    <div v-if="!wallet.active" class="m-vx2">
      <donut-alert
        title="Your points are currently disabled."
        subtitle="Ask your admin to turn them back on so you can use them again."
      />
    </div>
    <div v-else class="m-tx2">
      <div v-emoji="welcome" class="t" />
      <div v-if="desc" class="t m-tx2">{{ desc }}</div>
    </div>
    <div v-if="showShoutoutsBalance" class="f-i f-ac f-gap-8 b-1 br-12 p-x1 m-tx2">
      <span class="t">You have <span class="demibold">{{ wallet.current_givable.toLocaleString() }} {{ pointsName }}</span></span>
      <span v-emoji="`:${emoji}:`" class="t lh-1" />
      <span class="t">left to give this {{ period }}</span>
    </div>
    <div class="f f-gap-12 m-tx3">
      <shoutouts-profiles-stat-box title="You Gave" :stat="wallet.lifetime_given_out" :emoji="emoji" icon="BanknoteArrowUp" />
      <shoutouts-profiles-stat-box title="You Received" :stat="wallet.lifetime_redeemable" :emoji="emoji" icon="BanknoteArrowDown" />
      <shoutouts-profiles-stat-box
        v-if="props.rewardsProgram"
        title="Reward Balance"
        :stat="wallet.current_redeemable"
        :emoji="emoji"
        icon="Wallet"
        :primary="true"
        :button="{label: 'Store', href: '/rewards', disabled: !wallet.active, classes: !wallet.active && 'pe-n'}"
        :disabled="!wallet.active"
      />
    </div>
    <!-- Nest in a div to comply with odd rules to make it fit the content instead of stretching past it. -->
    <div><div class="separator m-tx4" /></div>
    <div class="w-872 f-sb f-ac m-vx3">
      <div class="t dark-purple upcase">{{ $i18n.t('shoutouts.shoutout', { count: 2 }) }}</div>
      <button v-if="allowPrivateShoutouts" @click="openPrivateShoutoutsModal">DM a {{ capitalize($i18n.t('shoutouts.shoutout', { count: 1 })) }}</button>
    </div>
    <shoutouts-profiles-wall v-if="props.shoutoutsProgram" />
    <div v-else>
      <div class="f-sb m-vx2">
        <div class="t">{{ shoutoutsAdCopy }}</div>
        <div class="f-je w-x80 m-lx1">
          <a
            href="https://help.donut.ai/en/articles/11368218-getting-started-donut-shoutouts"
            target="blank"
            class="inv button"
          >How it works</a>
          <button v-if="props.admins" class="m-lx1" @click="openAdminModal">Tell your admin</button>
          <a v-else href="/shoutouts" class="button m-lx1">Go to {{ featureName }}</a>
        </div>
      </div>
      <cdn-img class="w-100p" path="graphics/shoutouts/landing-example" />
    </div>
  </div>
</template>

<script setup>
  import { computed, inject, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize } from 'lodash';
  import useClientState from 'composables/client-state';

  const { state, commit, dispatch } = useStore();
  const { getQueryParamBool } = useClientState();

  const $i18n = inject('$i18n');
  const props = defineProps({
    user: { type: Object, required: true },
    pointsConfig: { type: Object, default: null },
    shoutoutsProgram: { type: Object, default: null },
    showShoutoutsBalance: { type: Boolean, default: false },
    rewardsProgram: { type: Object, default: null },
    admins: { type: Array, default: null },
  });

  const featureName = $i18n.t('shoutouts.feature');

  // Page Version
  const nouns = (props.shoutoutsProgram ? ['point', 'shoutout'] : ['point']).map((noun) => $i18n.t(`shoutouts.${noun}`, { count: 2 }));

  // Admin Viewing
  const selfViewing = computed(() => state.currentUser.id === props.user.id);
  const adminViewingCopy = computed(() => `As an Admin, you can view other people's ${nouns.join(' and ')}.`);
  const onUserSelect = (user) => {
    window.donut.track('Shoutouts Profile — Navigated to Other User', { userId: user.id, source: 'profile' });
    window.location.href = `/shoutouts/profile?user_id=${user.id}`;
  };

  // Stats
  const emoji = computed(() => props.pointsConfig.emoji);
  const wallet = computed(() => props.user.points_wallet);

  const period = computed(() => (props.shoutoutsProgram?.renewal_period === 'monthly' ? 'month' : 'week'));
  const pointsName = computed(() => (wallet.value.current_givable === 1 ? props.pointsConfig.points_name : props.pointsConfig.points_name_plural));

  // Page Copy
  const title = computed(() => {
    const capNouns = nouns.map(noun => _.startCase(noun)).join(' & ');
    if (selfViewing.value) {
      return `My ${capNouns}`;
    }
    return `${props.user.name}'s ${capNouns}`;
  });
  const welcome = computed(() => `:wave: Welcome, ${props.user.first_name || props.user.name}!`);
  const desc = computed(() => {
    // Only advertise a points source when that source is actually turned on for the team, so we don't
    // promise points for Intros to teams that have points for Intros disabled.
    const sources = [];
    if (props.pointsConfig.award_points_for_intros) sources.push('participating in Donut intros');
    if (props.shoutoutsProgram?.state === 'active') sources.push(`receiving ${$i18n.t('shoutouts.shoutout', { count: 2 })} from your teammates`);
    return sources.length ? `Earn points by ${sources.join(' or ')}.` : '';
  });

  // No Shoutouts Version
  const shoutoutsAdCopy = computed(() => {
    if (state.currentUser.shoutouts_admin) {
      // Since this copy is being rendered, Shoutouts isn't active. Since the user is a shoutouts_admin, the team is premium and can use Shoutouts.
      return `${featureName} is part of your plan but hasn't been set up yet. Donut makes it easy for your team to recognize great work and `
        + `celebrate wins with ${featureName}: personalized messages that build positivity and strengthen your culture, all within Slack.`;
    }
      const copy = `Get and give recognition points in Slack with ${featureName}. To get started, let your admin know you're interested in ${featureName}.`;
      return state.currentTeam.premium_account ? (`${copy} ${featureName} is included in your plan.`) : copy;
  });
  const openAdminModal = () => {
    commit('updateModal', {
      visible: true,
      subcomponent: 'shoutouts-profiles-modals-tell-admins',
      data: { admins: props.admins },
    });
  };

  // Private Shoutouts Modal
  const allowPrivateShoutouts = computed(() => wallet.value.active && props.shoutoutsProgram?.state === 'active');
  const openPrivateShoutoutsModal = () => {
    if (allowPrivateShoutouts.value) {
      commit('updateModal', {
        visible: true,
        subcomponent: 'shoutouts-profiles-modals-private-shoutout',
        size: 'xl',
        data: {
          pointsConfig: props.pointsConfig,
          shoutoutsProgram: props.shoutoutsProgram,
        },
      });
    }
  };

  // Lifecycle Hooks
  onMounted(() => {
    if (getQueryParamBool('open_private_shoutouts')) {
      if (!wallet.value.active) {
        // This flow happens when a user clicks a "DM a Shoutout" button in Slack. If they are inactive, they shouldn't
        // be getting any more of these sent their way, but just in case they click an old one...
        const toast = `${featureName} is disabled for you. Can't DM a ${$i18n.t('shoutouts.shoutout', { count: 1 })}.`;
        dispatch('setErrorToast', toast);
      } else {
        openPrivateShoutoutsModal();
      }
    }
  });
</script>
